import type { FC } from "react";
import { memo, useEffect } from "react";

import Typography from "@/design-system/Typography";
import Carousel from "@/design-system/carousel";
import { languageSelector, useViewportInfoContext } from "@/src/contexts";
import { FlyerSliderResources } from "@/src/sponsored-brand/FlyerSlider.Resources";
import { FlyerSliderResourcesEn } from "@/src/sponsored-brand/FlyerSlider.Resources.En";
import useAjax from "@Components/Hooks/useAjax";
import { Loading } from "@Components/Loading";
import * as stylex from "@stylexjs/stylex";

import { ComposableSpacing, type QueryXsMax, genericStyles } from "../../styles/tokens.stylex";
import type { IDiscountNavigationResultExtension } from "../discount/DiscountNavigation";
import { add } from "../discount/DiscountNavigationLib";
import type { IFlyerItem } from "./FlyerItem";
import { FlyerItem } from "./FlyerItem";

export interface IFlyersSlider {
    ajaxAction: string;
    data: IFlyersSliderResult;
    searchText: string;
    disableLazyLoading: boolean;
    gotoFlyer: (url: string) => void;
}

interface IFlyersSliderResult extends IDiscountNavigationResultExtension {
    flyers: IFlyerItem[];
}

const arePropsEqual = (prevProps: IFlyersSlider, nextProps: IFlyersSlider) =>
    nextProps.searchText === prevProps.searchText;

const FlyersSlider: FC<IFlyersSlider> = memo((props) => {
    const { isMobile, isMobileOrTablet } = useViewportInfoContext();

    const resources = languageSelector(FlyerSliderResources, FlyerSliderResourcesEn);

    const { request, isLoading, data, errorContainer } = useAjax<IFlyersSliderResult>({
        ajaxAction: props.ajaxAction,
        success: (response: IFlyersSliderResult): void => {
            if (response.flyers && response.flyers.length > 0) {
                add(response.navigation);
            }
        },
    });

    useEffect(() => {
        if (!props.data) request({ searchText: props.searchText || "" });
    }, [props.searchText]);

    if (data?.flyers?.length <= 0) {
        if (isLoading) {
            return (
                <div {...stylex.props(styles.loadingState)}>
                    <Loading isModal={false} noModalLayer={false} />
                </div>
            );
        }

        return (
            <Typography textElement="h2" fontStyle="BodyL" styleX={styles.noData}>
                {resources.noFlyersFound}
            </Typography>
        );
    }

    return (
        <div>
            <Carousel
                showArrows={!isMobile}
                centerMode={isMobile}
                itemsToShow={isMobileOrTablet ? 1 : 3}
                fullWidth={isMobileOrTablet}
            >
                {data?.flyers.map((currentValue, index) => (
                    <FlyerItem {...currentValue} key={index} />
                ))}
            </Carousel>
            {errorContainer}
        </div>
    );
}, arePropsEqual);

const xsMax: QueryXsMax = "@media (max-width: 759px)";

const styles = stylex.create({
    loadingState: {
        alignItems: "center",
        display: "flex",
        justifyContent: "center",
    },

    noData: {
        borderColor: "lightgray",
        borderRadius: genericStyles.borderRadiusS1,

        padding: `${ComposableSpacing.s6} ${ComposableSpacing.s2}`,
        borderStyle: "solid",
        borderWidth: "1px",
        marginBlock: "50px",
        marginInline: "0",
        alignItems: "center",
        display: "flex",
        fontSize: { default: "1.625rem", [xsMax]: "1rem" },
        justifyContent: "center",
        textTransform: "none",
        verticalAlign: "middle",
    },
});

export default FlyersSlider;
